import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { InjectableSuperclass } from '@sama/ng-core';
import { combineLatest, from, Observable, of, switchMap } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { CustomIconDirective } from '../../../cross-team-shared/icons/custom-icon.directive';
import { keys } from '../../../cross-team-shared/misc-utils/keys';
import {
  isOrthographicVideoOutput,
  isPointCloudOutput,
} from '../../../cross-team-shared/misc-utils/output-utils';
import { Output } from '../../../team-apis/otterdam/outputs/output';
import { SplitViewService } from '../../h3ml/dsl/sensor-fusion/split-view/split-view.service';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { FusionManager } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager';
import { FusionManagerFactory } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager-factory.service';
import { FusionMinion } from '../../outputs/annotation/annotation-shared/fusion/fusion-minion';
import { ShapeSelectionService } from '../../outputs/annotation/annotation-shared/utils/shape-selection.service';
import { projectionIndicatorIcon } from '../../shared/icons/output-tabs/projection-indicator-icon';
import { RootUndoService } from '../../shared/misc-services/root-undo.service';

@Component({
  selector: 'h3-output-tab',
  templateUrl: './output-tab.component.html',
  styleUrl: './output-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, MatIcon, MatIcon, CustomIconDirective],
  hostDirectives: [MatTooltip],
  host: {
    class: 'subtitle-03',
  },
})
export class OutputTabComponent
  extends InjectableSuperclass
  implements OnInit, OnChanges
{
  @Input({ required: true }) output!: Output;
  @Input({ required: true }) showOutputName!: boolean;
  @Input() @HostBinding('class.selected') selected = false;

  position?: number;
  hasShapeIndicator$!: Observable<boolean>;
  hasProjectionIndicator$ = of(false);
  is3DOutput!: boolean;
  projectionIndicatorIcon = projectionIndicatorIcon;

  private store = inject(TaskWorkspaceStore);
  private tooltip = inject(MatTooltip);
  private splitViewService = inject(SplitViewService);
  private fusionManagerFactory = inject(FusionManagerFactory);
  private undoService = inject(RootUndoService);
  private fusionManager!: FusionManager;

  @HostListener('click')
  selectTab() {
    this.store('splitViewOutputName').set(this.output.name);

    if (!this.selected) {
      this.splitViewService.changeActiveOutput(this.output.name, 'click_sv');
    }
  }

  ngOnInit() {
    this.tooltip.message = this.output.name;

    this.position =
      this.store
        .state()
        .outputsByDisplayOrder.findIndex(
          (output) => output.name === this.output.name,
        ) + 1;

    this.fusionManager = this.fusionManagerFactory.getManager(this.output.id);

    this.hasShapeIndicator$ = this.buildHasShapeIndicator$();

    this.is3DOutput = isPointCloudOutput(this.output);

    if (!this.is3DOutput && !isOrthographicVideoOutput(this.output)) {
      this.hasProjectionIndicator$ = this.buildHasProjectionIndicator$();
    }
  }

  ngOnChanges() {
    this.tooltip.disabled = this.showOutputName;
  }

  private buildHasShapeIndicator$() {
    return from(
      this.fusionManager.sleepUntilMinionIsAvailable(this.output.id),
    ).pipe(
      switchMap((minion) =>
        this.getSelectedShapeIndexes$(minion).pipe(
          switchMap((selectedShapeIndexes) =>
            this.hasVisibleShape$(selectedShapeIndexes, minion),
          ),
        ),
      ),
      share(),
    );
  }

  private getSelectedShapeIndexes$(minion: FusionMinion) {
    return minion.injector
      .get(ShapeSelectionService)
      .getSelectionForOutput$(false)
      .pipe(map((selection) => keys(selection)));
  }

  private hasVisibleShape$(
    selectedShapeIndexes: number[],
    minion: FusionMinion,
  ) {
    return combineLatest([
      this.fusionManager.getFrameNumberForOutput$(this.output.name),
      this.undoService.state$,
    ]).pipe(
      map(([frameNumber]) =>
        selectedShapeIndexes.some(
          (shapeIndex) =>
            minion.contentConnector.determineLocation(frameNumber, shapeIndex)
              .visibility > 0,
        ),
      ),
    );
  }

  private buildHasProjectionIndicator$() {
    return this.hasShapeIndicator$.pipe(
      switchMap((hasShapeIndicator) =>
        hasShapeIndicator
          ? of(false)
          : from(this.fusionManager.sleepUntilPrimaryIsAvailable()).pipe(
              switchMap((minion) =>
                this.getSelectedShapeIndexes$(minion).pipe(
                  switchMap((selectedShapeIndexes) =>
                    this.hasVisibleProjection$(selectedShapeIndexes, minion),
                  ),
                ),
              ),
            ),
      ),
    );
  }

  private hasVisibleProjection$(
    selectedShapeIndexes: number[],
    minion: FusionMinion,
  ) {
    const contentConnector = minion.getPointCloudContentConnector();

    return combineLatest([
      this.fusionManager.getFrameNumberForOutput$(minion.output.name),
      this.undoService.state$,
    ]).pipe(
      map(([frameNumber]) =>
        selectedShapeIndexes.some(
          (shapeIndex) =>
            contentConnector.determineLocation(frameNumber, shapeIndex)
              .visibility > 0 &&
            contentConnector.getProjectedRectanglePoints(
              shapeIndex,
              this.output.name,
            ),
        ),
      ),
    );
  }
}
